import {
	I18nFormProvider,
	IDrawerDataListColumn,
	PaginatedDrawerAutoDataList,
	Tag,
	laravelPaginatedListFetchConfig,
	useDynamicConfig,
	useHasPermission,
	useLaravelCrudEntity,
} from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { eventService } from "../../../shared/api/eventService";
import { eventTypeService as sharedEventTypeService } from "../../../shared/api/eventTypeService";
import { PermissionName } from "../../../shared/types/permission";
import { eventTypeService } from "../shared/api/eventTypeService";
import { EventTypeFormValues, EventTypeIndexResource, EventTypeSaveRequest } from "../shared/types/eventType";
import { EventTypeCreateStepperDrawer } from "./EventTypeCreateStepperDrawer";
import { EventTypeListContextMenu } from "./EventTypeListContextMenu";
import { EventTypeProvider } from "./EventTypeProvider";
import { EventTypeUpdateDrawer } from "./EventTypesDrawer";

const Chip = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: ${({ theme }) => theme.spacing(2)};
	align-items: center;
	justify-content: flex-end;
`;

const mapToRequest = (formValues: EventTypeFormValues): EventTypeSaveRequest => {
	const {
		fallbackLanguage,
		image,
		eventRole,
		costCategories,
		costCategoryRequirements,
		userResponsibilities,
		userResponsibilityRequirements,
		feedback,
		eventGoalRatingMetricWeight,
		...rest
	} = formValues;

	if (!fallbackLanguage || !eventRole) {
		throw new Error("Missing parameter detected");
	}
	if (costCategories.length !== costCategoryRequirements.length) {
		throw new Error("Incorrect mapping of cost category requirements detected");
	}

	if (userResponsibilities.length !== userResponsibilityRequirements.length) {
		throw new Error("Incorrect mapping of user responsibility requirements detected");
	}

	return {
		...rest,
		feedbackIds: feedback.map((f) => f.id),
		eventRole,
		fallbackLanguageIsoCode: fallbackLanguage.isoCode,
		imageId: !image || image?.id === -1 ? null : image.id,
		costCategoryRequirements: costCategoryRequirements.map((r) => ({
			costCategoryId: r.costCategory.id,
			requirement: r.requirement,
		})),
		userResponsibilityRequirements: userResponsibilityRequirements.map((r) => ({
			userResponsibilityId: r.userResponsibility.id,
			requirement: r.requirement,
		})),
		eventGoalRatingMetricWeight:
			rest.eventGoalRatingMetricRequirement === "prohibited" ? 0 : eventGoalRatingMetricWeight,
	};
};

export const EventTypesList: FC = () => {
	const { t } = useTranslation();
	const { languageSettings } = useDynamicConfig();
	const crudOperations = useLaravelCrudEntity(
		eventTypeService,
		{
			entityName: t("Event type"),
			messages: {
				create: t("Event type successfully created."),
				update: t("Event type successfully updated."),
				delete: t("Event type successfully deleted."),
			},
		},
		[],
		[eventService.baseKey, ["event-types", "options"], sharedEventTypeService.quickFilterKey],
	);

	const canCreate = useHasPermission<PermissionName>(["eventTypes.create"]);
	const canUpdate = useHasPermission<PermissionName>(["eventTypes.update"]);

	const dataListColumns: IDrawerDataListColumn<EventTypeIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Name"),
				valueKey: "name",
				sortable: true,
				type: "value",
				render: (type) => (
					<Chip>
						{type.name}
						{type.isDefault && (
							<Tag size="small" color="primary" name="isDefault">
								{t("Default")}
							</Tag>
						)}
					</Chip>
				),
			},
			{
				heading: t("Event role"),
				valueKey: "eventRole",
				type: "value",
				render: (eventType) => t(eventType.eventRole),
			},
			{
				heading: "",
				type: "actions",
				size: "max-content",
				action: (eventType, openDrawer) => (
					<EventTypeListContextMenu
						eventTypeId={eventType.id}
						onEdit={openDrawer}
						onDelete={() => crudOperations.deleteEntity(eventType.id)}
					/>
				),
			},
		],
		[t, crudOperations],
	);

	return (
		<PaginatedDrawerAutoDataList
			canUpdate={canUpdate}
			canCreate={canCreate}
			translations={{ title: t("Event types"), empty: t("No event types available") }}
			columns={dataListColumns}
			listFetchConfig={laravelPaginatedListFetchConfig(eventTypeService.listKey, eventTypeService.index)}
			renderCreateDrawer={(drawerProps) => (
				<EventTypeProvider>
					<I18nFormProvider initialLanguage={languageSettings.fallback}>
						<EventTypeCreateStepperDrawer {...drawerProps} handleClose={drawerProps.onClose} />
					</I18nFormProvider>
				</EventTypeProvider>
			)}
			renderUpdateDrawer={(drawerProps) => (
				<EventTypeUpdateDrawer
					{...drawerProps}
					onSubmit={(formValues) => crudOperations.updateEntity(drawerProps.id, mapToRequest(formValues))}
				/>
			)}
			initialSorting={{ column: "name", direction: "asc" }}
			initialSearch={{
				search: "",
				searchFields: [
					{ name: "event_type_translations.name", label: t("Name") },
					{ name: "eventRole", label: t("Event role") },
				],
			}}
		/>
	);
};
